import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { PageContextService } from './page-context.service';
import { PropertyManagementCompanyService } from '../property-management-company/property-management-company.service';

/** Community a PMC-scoped drawer is drilled into (SD-9209). */
export interface GableDrillDownOrg {
    id: number;
    name: string;
}

/** Single source of truth for Gable's scope (SD-9208/SD-9209): org | pmc-portfolio | pmc-drill. */
@Injectable({ providedIn: 'root' })
export class GableScopeService {
    /** `'org'`, `'pmc:{id}'` or `'pmc:{id}:org:{orgId}'` — emits when a scope boundary is crossed. */
    readonly scopeKey$: Observable<string>;

    /** Drill-down community, set from the drawer's community picker (SD-9209). */
    readonly drillDownOrg$ = new BehaviorSubject<GableDrillDownOrg | null>(null);

    private _pmcName: string | null = null;

    constructor(
        private _pageContext: PageContextService,
        private _pmc: PropertyManagementCompanyService,
    ) {
        this._pmc.propertyManagementCompany$.subscribe((pmc) => this._pmcName = pmc?.name ?? null);

        this.scopeKey$ = combineLatest([
            this._pageContext.context$,
            this._pmc.pmcId$,
            this.drillDownOrg$,
        ]).pipe(
            map(() => this.scopeKey),
            distinctUntilChanged(),
        );

        // Drill-down only survives within one PMC's /pmc pages — clear it on any other move.
        // Keyed on the URL-aware getter: pmcId$ is never seeded on super-admin /pmc/:id pages.
        combineLatest([this._pageContext.context$, this._pmc.pmcId$])
            .pipe(
                map(() => this.pmcId),
                distinctUntilChanged(),
            )
            .subscribe(() => this.clearDrillDown());
    }

    /** PMC id when PMC-scoped (portfolio OR drill-down), else null (org-scoped). */
    get pmcId(): number | null {
        const url = this._pageContext.snapshot()?.url ?? '';
        if (!url.startsWith('/pmc')) {
            return null;
        }
        // URL is authoritative — some /pmc/:pmcId pages (super-admin details) never
        // seed pmcId$, and no-id /pmc pages (/pmc/list) must stay org-scoped rather
        // than inherit a stale subject value.
        const fromUrl = /^\/pmc\/(\d+)(?:[/;?]|$)/.exec(url);
        return fromUrl !== null ? Number(fromUrl[1]) : null;
    }

    /** Name of the PMC when its record is already loaded, else null. */
    get pmcName(): string | null {
        return this._pmcName;
    }

    get isPmcScoped(): boolean {
        return this.pmcId !== null;
    }

    /** PMC id only in portfolio scope — null once drilled into a community. */
    get portfolioPmcId(): number | null {
        const pmcId = this.pmcId;
        return pmcId !== null && this.drillDownOrg$.value === null ? pmcId : null;
    }

    get drillDownOrg(): GableDrillDownOrg | null {
        return this.pmcId !== null ? this.drillDownOrg$.value : null;
    }

    /** Org id to use on org-family routes while drilled in, else null. */
    get drillDownOrgId(): number | null {
        return this.drillDownOrg?.id ?? null;
    }

    enterDrillDown(org: GableDrillDownOrg): void {
        this.drillDownOrg$.next(org);
    }

    clearDrillDown(): void {
        if (this.drillDownOrg$.value !== null) {
            this.drillDownOrg$.next(null);
        }
    }

    /** Synchronous form of `scopeKey$`. */
    get scopeKey(): string {
        const pmcId = this.pmcId;
        if (pmcId === null) {
            return 'org';
        }
        const drill = this.drillDownOrg$.value;
        return drill !== null ? `pmc:${pmcId}:org:${drill.id}` : `pmc:${pmcId}`;
    }
}
